import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, Database, Key, Cpu, RefreshCw, XCircle } from 'lucide-react';
import ToolCard from '../shared/ToolCard';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const InfrastructureTools = () => {
  const [selectedTool, setSelectedTool] = useState(null);
  const [isLoading, setIsLoading] = useState({});
  const [serverStats, setServerStats] = useState(null);
  const [maintenanceStatus, setMaintenanceStatus] = useState({
    isLoading: false,
    isEnabled: false,
    message: ''
  });

  const infrastructureTools = [
    {
      id: 'serverTools',
      icon: Settings,
      label: 'Server Management',
      description: 'Server statistics and maintenance mode controls',
      action: 'Manage Server'
    },
    {
      id: 'cacheControl',
      icon: Database,
      label: 'Cache Management', 
      description: 'Clear and manage application caches',
      action: 'Manage Cache'
    },
    {
      id: 'apiKeys',
      icon: Key,
      label: 'API Key Management',
      description: 'Create and manage API keys for system access',
      action: 'Manage Keys'
    }
  ];

  const handleToolClick = (tool) => {
    setSelectedTool(selectedTool === tool.id ? null : tool.id);
  };

  const getServerStats = async () => {
    try {
      setIsLoading({ ...isLoading, stats: true });
      const response = await api.get('/api/dev/server-stats/');
      setServerStats(response.data);
      toast.success('Server statistics loaded');
    } catch (error) {
      console.error('Error loading server stats:', error);
      toast.error('Failed to load server statistics');
    } finally {
      setIsLoading({ ...isLoading, stats: false });
    }
  };

  const toggleMaintenanceMode = async () => {
    try {
      setMaintenanceStatus({ ...maintenanceStatus, isLoading: true });
      const response = await api.post('/api/dev/maintenance-mode/');
      
      setMaintenanceStatus({
        isLoading: false,
        isEnabled: response.data.enabled,
        message: `Maintenance mode ${response.data.enabled ? 'enabled' : 'disabled'}`
      });
      
      toast.success(
        <div>
          <p className="font-bold">Maintenance mode {response.data.enabled ? 'enabled' : 'disabled'}</p>
          <p className="text-sm mt-1">{response.data.message || ''}</p>
        </div>
      );
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      setMaintenanceStatus({
        isLoading: false,
        isEnabled: maintenanceStatus.isEnabled,
        message: 'Error toggling maintenance mode'
      });
      toast.error('Failed to toggle maintenance mode');
    }
  };

  const clearCache = async (cacheType) => {
    try {
      setIsLoading({ ...isLoading, [cacheType]: true });
      await api.post('/api/dev/clear-cache/', { cache_type: cacheType });
      toast.success(`${cacheType} cache cleared successfully`);
    } catch (error) {
      console.error(`Error clearing ${cacheType} cache:`, error);
      toast.error(`Failed to clear ${cacheType} cache`);
    } finally {
      setIsLoading({ ...isLoading, [cacheType]: false });
    }
  };

  const renderToolContent = (toolId) => {
    switch (toolId) {
      case 'serverTools':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6 space-y-6">
            <h3 className="text-xl font-bold text-gray-800">Server Management</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={getServerStats}
                disabled={isLoading.stats}
                className="p-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
              >
                {isLoading.stats ? (
                  <RefreshCw className="animate-spin" size={20} />
                ) : (
                  <Cpu size={20} />
                )}
                <span>Load Server Stats</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={toggleMaintenanceMode}
                disabled={maintenanceStatus.isLoading}
                className="p-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors flex items-center justify-center space-x-2"
              >
                {maintenanceStatus.isLoading ? (
                  <RefreshCw className="animate-spin" size={20} />
                ) : (
                  <Settings size={20} />
                )}
                <span>Toggle Maintenance</span>
              </motion.button>
            </div>

            {serverStats && (
              <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-xl">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-gray-800">Server Statistics</h4>
                  <button
                    onClick={() => setServerStats(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <XCircle size={20} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(serverStats).map(([key, value]) => (
                    <div key={key} className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                      <h5 className="font-medium text-gray-700 capitalize">{key.replace('_', ' ')}</h5>
                      <p className="text-sm text-gray-600 mt-1">
                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'cacheControl':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Cache Management</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['template', 'view', 'session', 'permissions', 'all'].map(cacheType => (
                <motion.button
                  key={cacheType}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => clearCache(cacheType)}
                  disabled={isLoading[cacheType]}
                  className="p-4 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors flex items-center justify-center space-x-2"
                >
                  {isLoading[cacheType] ? (
                    <RefreshCw className="animate-spin" size={20} />
                  ) : (
                    <Database size={20} />
                  )}
                  <span>Clear {cacheType.charAt(0).toUpperCase() + cacheType.slice(1)}</span>
                </motion.button>
              ))}
            </div>
          </div>
        );

      case 'apiKeys':
        return (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">API Key Management</h3>
            <p className="text-gray-600 mb-4">
              API key management interface would go here. This would typically include:
            </p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Create new API keys</li>
              <li>• View existing keys</li>
              <li>• Revoke/disable keys</li>
              <li>• Set key permissions</li>
            </ul>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {infrastructureTools.map((tool) => (
          <ToolCard
            key={tool.id}
            tool={tool}
            onClick={handleToolClick}
            isLoading={isLoading[tool.id]}
            className={selectedTool === tool.id ? 'ring-2 ring-primary-500' : ''}
          />
        ))}
      </div>

      {/* Render selected tool content */}
      {selectedTool && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          {renderToolContent(selectedTool)}
        </motion.div>
      )}
    </div>
  );
};

export default InfrastructureTools;
